import { H3, H5 } from '@wheely/ui-kit';
import cx from 'classnames';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { IDriver } from '@/modules/driver/model/driver';
import { IWorkloadSummary } from '@/modules/driver/model/driver-workload';
import { getCityTimezone } from '@/modules/partner/store/selectors';
import { TCountryCodes } from '@/shared/models/country-code';
import { AccessStatus } from '@/shared/view/AccessStatus';
import { StepLayout } from '@/shared/view/StepLayout';
import { TransferStatus } from '@/shared/view/TransferStatus';
import { IDocsListItem } from '@src-common/view/components/DocsList';

import { Avatar } from '../Chauffeurs/components/Avatar';

import { BlockInfo } from './components/BlockInfo';
import type { IProps as IDocsProps } from './components/TabDocs';
import { Docs } from './components/TabDocs';
import type { IProps as IOverviewProps } from './components/TabOverview';
import { Overview } from './components/TabOverview';
import type { IProps as IWorkingHoursProps } from './components/TabWorkingHours';
import { WorkingHours } from './components/TabWorkingHours';
import s from './view.module.css';

interface IProps<T extends IDocsListItem> {
  driver: IDriver;
  region: TCountryCodes;
  onClickBack: () => void;
  docs: IDocsProps<T>['docs'];
  onUploadDoc: IDocsProps<T>['onUploadDoc'];
  onChangeContract: IDocsProps<T>['onChangeContract'];
  onClickBlock: IOverviewProps['onClickBlock'];
  onClickDismiss: IOverviewProps['onClickDismiss'];
  isPending: IOverviewProps['isPending'];
  workloads: IWorkingHoursProps['workloads'];
  workloadSummary: IWorkloadSummary | null;
  workingHoursWeek: IWorkingHoursProps['week'];
  onWorkingHoursWeekChange: IWorkingHoursProps['onWeekChange'];
}

type Tab = '#overview' | '#docs' | '#working-hours';

const DEFAULT_TAB: Tab = '#overview';

const tabs = defineMessages<Tab>({
  '#overview': {
    defaultMessage: 'Overview',
    id: 'SAseai',
    description: 'Driver profile: Overview tab',
  },
  '#docs': {
    defaultMessage: 'Documents',
    id: '49Lusj',
    description: 'Driver profile: Documents tab',
  },
  '#working-hours': {
    defaultMessage: 'Working hours',
    id: 'DO9fZh',
    description: 'Driver profile: Working hours tab',
  },
});

const View = <T extends IDocsListItem>({
  driver,
  docs,
  onUploadDoc,
  onClickBack,
  onChangeContract,
  onClickBlock,
  onClickDismiss,
  isPending,
  region,
  workloads,
  workloadSummary,
  workingHoursWeek,
  onWorkingHoursWeekChange,
}: IProps<T>) => {
  const timezone = useSelector(getCityTimezone);
  const location = useLocation();
  const activeTab = location.hash in tabs ? (location.hash as Tab) : DEFAULT_TAB;

  return (
    <StepLayout onClickBack={onClickBack} hideProgress={true}>
      <div className={s.root}>
        <div className={s.topRow}>
          <Avatar photoURL={driver.photoURL} />
          <H3 className={s.name}>
            {driver.firstName} {driver.lastName}
          </H3>
          <div className={s.tags}>
            <AccessStatus status={driver.accessStatus} />
            {driver.transferRequest && (
              <TransferStatus date={driver.transferRequest.leavingAtDate} timezone={timezone} />
            )}
          </div>
          {driver.blockInfo?.blockedBy === 'partner' ? (
            <BlockInfo blockInfo={driver.blockInfo} />
          ) : null}
        </div>
        <div className={s.tabsFiller}>
          <div className={s.tabsWrapper}>
            <div className={s.tabs}>
              {Object.entries(tabs).map(tab => (
                <div key={tab[0]} className={s.tabWrapper}>
                  <Link to={tab[0]} replace={true} className={s.link}>
                    <H5
                      capitalize={true}
                      className={cx(s.tab, { [s.active]: tab[0] === activeTab })}
                    >
                      <FormattedMessage {...tab[1]} />
                    </H5>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
        {activeTab === '#overview' && (
          <Overview
            driver={driver}
            onClickBlock={onClickBlock}
            onClickDismiss={onClickDismiss}
            isPending={isPending}
          />
        )}
        {activeTab === '#docs' && (
          <Docs
            driver={driver}
            docs={docs}
            onUploadDoc={onUploadDoc}
            region={region}
            onChangeContract={onChangeContract}
          />
        )}
        {activeTab === '#working-hours' && (
          <WorkingHours
            workloads={workloads}
            week={workingHoursWeek}
            summary={workloadSummary}
            onWeekChange={onWorkingHoursWeekChange}
          />
        )}
      </div>
    </StepLayout>
  );
};

export { View };
